import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../use_cases/UseCasePages.css';
import shieldLogo from '../../assets/shield_logo.png';
import cursorIcon from '../../assets/tools/cursor.png';
import vscodeIcon from '../../assets/tools/vscode.png';
import claudeIcon from '../../assets/tools/anthropic.png';
import copilotIcon from '../../assets/tools/githubcopilot.png';
import gitIcon from '../../assets/tools/githubcircle.png';
import mcpIcon from '../../assets/tools/mcp_white.png';
import webIcon from '../../assets/tools/globe.png';
import appIcon from '../../assets/tools/app_connection.png';
import infraIcon from '../../assets/tools/stack.png';
import dbIcon from '../../assets/tools/database.png';
import prodIcon from '../../assets/tools/deploy.png';

const CodingAgentsGuide = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const sectionIds = ['uc-hero', 'section-overview', 'section-guardrails', 'section-claudemd', 'section-gitignored', 'section-permissions', 'section-compaction', 'section-npmtest', 'section-supplychain', 'section-mcp', 'section-indirect', 'section-commits', 'section-coverage', 'section-cta'];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      setCanScrollUp(scrollTop > 10);
      setCanScrollDown(scrollTop + winHeight < docHeight - 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const scrollToSection = useCallback((direction) => {
    const currentScroll = window.scrollY;
    const nav = document.querySelector('.nav-container');
    const navH = nav ? nav.offsetHeight : 64;
    const positions = sectionIds.map(id => {
      const el = document.getElementById(id);
      if (!el) return null;
      const title = el.querySelector('h2, p');
      const target = title || el;
      return { scrollTo: target.getBoundingClientRect().top + window.scrollY - navH - 40 };
    }).filter(Boolean);

    if (direction === 'down') {
      const next = positions.find(p => p.scrollTo > currentScroll + 10);
      if (next) window.scrollTo({ top: next.scrollTo, behavior: 'smooth' });
    } else {
      const prev = [...positions].reverse().find(p => p.scrollTo < currentScroll - 10);
      if (prev) window.scrollTo({ top: prev.scrollTo, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <div className="scroll-float-wrap">
        {canScrollUp && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('up')} aria-label="Scroll up">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,6 4,16 20,16" fill="currentColor"/></svg>
          </button>
        )}
        {canScrollDown && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('down')} aria-label="Scroll down">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,18 4,8 20,8" fill="currentColor"/></svg>
          </button>
        )}
      </div>

      <section id="uc-hero" className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <h1 className="hero-page-title">AI Coding Agents</h1>
      </section>

      <section className="ucp-content">
        <div className="ucp-layout">
          <h2 className="ucp-heading ucp-heading-balance">10 Claude Code Risks Your Security Stack Cannot See</h2>
          <p className="ucp-description">
            Your CASB sees an API call. Your SWG sees TLS. Your EDR sees a signed binary a developer chose to run. None of them see a coding agent read a credential file, rewrite its own settings, or follow instructions planted in a repository. Bltz Shield runs on the endpoint, inside the agent, before the prompt leaves the machine.
          </p>

          <div className="ucp-cta" style={{ marginTop: 0, marginBottom: 20 }}>
            <button className="ucp-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              Get a Live Demo &rarr;
            </button>
          </div>

          {/* Overview */}
          <div id="section-overview" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">The Challenge: An Agent With Your Developer&apos;s Permissions</h2>
            <p className="ucp-section-desc">
              An AI coding agent is the first software most organizations have deployed that reads source and secrets, writes to disk, runs shell commands, connects to arbitrary tools, and does all of it autonomously with a developer&apos;s full permissions. It installs from a terminal, per developer, with no license seat to audit and no network appliance in the path. Traditional controls were built for a browser tab or a network egress point. A coding agent is neither.
            </p>
            <div className="ucp-arch">
              <div className="ucp-arch-col">
                <span className="ucp-arch-caption">Where developers work</span>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={claudeIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">Claude Code</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={cursorIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">Cursor</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={vscodeIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">VS Code</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={copilotIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">GitHub Copilot</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={gitIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">Git</span></div>
              </div>

                <div className="ucp-arch-conn">
                  <svg className="ucp-arch-link" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                    <path vectorEffect="non-scaling-stroke" d="M 0 16.7 C 45 16.7, 55 50, 100 50" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 33.3 C 45 33.3, 55 50, 100 50" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 50.0 C 45 50.0, 55 50, 100 50" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 66.7 C 45 66.7, 55 50, 100 50" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 83.3 C 45 83.3, 55 50, 100 50" />
                  </svg>
                </div>

              <div className="ucp-arch-core">
                <img src={shieldLogo} alt="Bltz AI" className="ucp-arch-core-logo" />
                <span className="ucp-arch-core-name">Bltz Shield</span>
                <span className="ucp-arch-core-sub">On the endpoint</span>
              </div>

                <div className="ucp-arch-conn">
                  <svg className="ucp-arch-link" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                    <path vectorEffect="non-scaling-stroke" d="M 0 50 C 45 50, 55 14.3, 100 14.3" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 50 C 45 50, 55 28.6, 100 28.6" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 50 C 45 50, 55 42.9, 100 42.9" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 50 C 45 50, 55 57.1, 100 57.1" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 50 C 45 50, 55 71.4, 100 71.4" />
                    <path vectorEffect="non-scaling-stroke" d="M 0 50 C 45 50, 55 85.7, 100 85.7" />
                  </svg>
                </div>

              <div className="ucp-arch-col">
                <span className="ucp-arch-caption">What the agent reaches</span>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={mcpIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">MCP servers</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={webIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">Web pages and fetched content</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={appIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">Applications, shells and files</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={infraIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">Infrastructure</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={dbIcon} alt="" className="ucp-arch-icon ucp-arch-icon-white" /></span><span className="ucp-arch-label">Databases</span></div>
                <div className="ucp-arch-item"><span className="ucp-arch-chip"><img src={prodIcon} alt="" className="ucp-arch-icon" /></span><span className="ucp-arch-label">Production</span></div>
              </div>
            </div>
            <p className="ucp-arch-note">Every prompt, tool call and response passes through Shield before it leaves the machine.</p>
          </div>

          <div id="section-guardrails" className="ucp-section">
            <h2 className="ucp-section-title">1. It can delete its own guardrails</h2>
            <p className="ucp-section-desc">A coding agent has write access to the filesystem, and its own configuration is a file on that filesystem. An agent that edits its settings removes every safety hook it runs under. Nothing errors and nothing turns red. The detections simply stop, and every tool call afterwards runs unsupervised and unrecorded. The same result is one shell command away, since redirecting output over the settings file strips the hooks without using an edit tool at all.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>The agent writes to its own settings file, or a shell command overwrites it, removing all policy hooks.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>Blocked outright, on both the edit path and the shell path. This is one of the few policies set to stop the action rather than record it.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>A local file write by a trusted process. It never crosses the network, so no CASB, gateway or DLP appliance is in the path.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-claudemd" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">2. It can be jailbroken by one line in CLAUDE.md</h2>
            <p className="ucp-section-desc">Coding agents read project instruction files from the repository and treat them as standing orders. That is the feature, and it is why they are useful. It also means a repository can carry instructions that override policy, request elevated tool permissions, or impersonate an administrator. The blast radius is what makes this different from an ordinary supply chain risk: a poisoned rules file does not affect its author, it affects every developer who clones the project, from the moment they open it.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>Instructions embedded in a project rules file grant elevated permissions or disable policy for anyone who opens the repository.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>Instruction files are scanned as they load, and embedded overrides are caught before the session starts.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>It is a text file in a git repository. It reads as documentation in review, and static analysis looks for vulnerable code, not for instructions aimed at a model.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-gitignored" className="ucp-section">
            <h2 className="ucp-section-title">3. It can be reconfigured in a gitignored file</h2>
            <p className="ucp-section-desc">Agent configuration does not have to live in a file anyone reviews. A local settings file listed in .gitignore never appears in a pull request, never reaches CI, and never shows up in a diff, yet it changes how the agent behaves on that machine. A developer can be handed one, or write one, and the team has no way to see it.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>Agent behaviour is changed through a local configuration file that is excluded from version control and invisible to review.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>Configuration reads and writes are inspected wherever they happen, and the session record captures what was in effect for that run.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>Review and CI only see what is committed. A gitignored file is invisible to both by design.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-permissions" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">4. It can run with permissions off. One flag.</h2>
            <p className="ucp-section-desc">Claude Code ships a flag that skips its own permission prompts. It exists for automation and it is a single argument on the command line. A developer under time pressure can turn off every confirmation the tool would otherwise ask for, and nothing about the session afterwards looks different.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>The agent is launched with permission prompts disabled, and every tool call proceeds without asking.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>Policy enforcement is independent of the agent’s own prompts, so the controls still apply to a session started with them turned off.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>The flag is a local process argument. Nothing observes how a developer chose to start a terminal program.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-compaction" className="ucp-section">
            <h2 className="ucp-section-title">5. It can resend your secret every turn</h2>
            <p className="ucp-section-desc">Long sessions do not keep every message. When the context window fills, the conversation is compressed into a summary and that summary is fed back in as the new starting point. Anything the session touched can survive that compression, including a credential that appeared in a file read or a command output an hour earlier. Controls that inspect prompts and tool calls have already passed on that content; the summary is a new artifact, generated afterwards, and it re-enters the vendor request path on the very next turn.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>An API key read early in a session is carried into the compaction summary and sent again later.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>The generated summary is scanned for secrets and personal data after compaction, before it is used again.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>No user typed it, no tool returned it and no file was opened. The model produced it, so input-side inspection has nothing to look at.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-npmtest" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">6. It can run any script npm test allows</h2>
            <p className="ucp-section-desc">An agent that is allowed to run tests is allowed to run whatever the test script contains. package.json can invoke any binary, fetch anything, and read any file the developer can. Approving the command approves everything downstream of it, and the contents of that script can change in a pull request without the approval being revisited.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>A permitted command runs arbitrary code through a script definition that was changed elsewhere.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>The command is recorded, its output is scanned for secrets and personal data before the model sees it, and unusually large output is flagged as a possible bulk extraction.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>The command looks routine. What runs underneath it is decided by a file, not by the command that was approved.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-supplychain" className="ucp-section">
            <h2 className="ucp-section-title">7. It can install tools no one on your team chose</h2>
            <p className="ucp-section-desc">Agents install packages to get their work done. An install from a non-standard index is the standard supply chain attack: a typosquatted or backdoored package runs arbitrary code on the developer machine, and then on the build runner, with no review step in between.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>A package is installed from an index that is not your organisation’s registry.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>Installs from non-standard registries are detected before the command runs.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>It is an ordinary developer action on an ordinary developer machine, indistinguishable from legitimate work at the network layer.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-mcp" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">8. It can hand a token to a rogue MCP server</h2>
            <p className="ucp-section-desc">Coding agents connect to external tools through MCP servers, and a server can prompt the user for input. A malicious or compromised server can use that channel to request a password, an API key or an access token, presented as a routine authentication step. There is no exploit involved: the user is asked, and answers. Local MCP servers are also the largest blind spot in AI governance today, because a server running over standard input and output has no OAuth grant to inventory and no browser tab to observe.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>A connected tool server asks the developer for a credential under the guise of signing in.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>Credential requests coming from tool servers are blocked before the developer sees them.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>A local server has no OAuth grant and no web traffic, so identity-based and browser-based discovery cannot see it at all.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-indirect" className="ucp-section">
            <h2 className="ucp-section-title">9. It can run commands hidden in a web page</h2>
            <p className="ucp-section-desc">When an agent fetches a page, the contents become instructions in its context. An attacker who controls a page, an issue comment or a dependency’s documentation can direct the agent without ever touching your network. The same channel is a route outward: a fetch to an attacker-controlled address is both the instruction and the exfiltration path.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>A fetched page carries instructions the agent follows, or serves as the destination for content leaving the machine.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>Fetch destinations are checked against your allowlist, and returned content is scanned before the model uses it.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>An ordinary outbound request from a developer machine to a public site. It looks identical to browsing.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          <div id="section-commits" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">10. It can commit unreviewed code as your dev</h2>
            <p className="ucp-section-desc">The agent works with the developer’s git identity. Code it writes is authored, committed and pushed under a person’s name, and the history gives no indication that a model produced it. Reviewers extend the trust they would give a colleague to output nobody read.</p>
            <div className="ucp-threat-grid">
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-blocked">Attack</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">What happens</span>
                  <p>Generated code enters the repository under a developer’s identity with no marker that it was machine written.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#10005;</span>
                  <span>Content is scanned before it reaches disk and again after the write, so secrets and personal data are caught before they can be committed, and the session record shows which files a run changed.</span>
                </div>
              </div>
              <div className="ucp-threat-card">
                <div className="ucp-threat-label ucp-label-tested">Why nothing else catches it</div>
                <div className="ucp-threat-example">
                  <span className="ucp-threat-tag">The gap</span>
                  <p>Git records an author, not an origin. Nothing downstream distinguishes typed code from generated code.</p>
                </div>
                <div className="ucp-threat-result">
                  <span className="ucp-result-icon">&#9888;</span>
                  <span>Bltz Shield sits on the endpoint, inside the agent.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Coverage */}
          <div id="section-coverage" className="ucp-section ucp-section-light">
            <h2 className="ucp-section-title">Where Bltz Shield Sits</h2>
            <p className="ucp-section-desc">
              Detection happens at the points in an agent&apos;s lifecycle where the risk actually appears, not at a network boundary the agent never crosses. That includes several places most tooling does not instrument at all.
            </p>
            <div className="ucp-compliance-grid">
              <div className="ucp-compliance-card">
                <h4>When a prompt is submitted</h4>
                <p>Secrets, regulated data, customer information, proprietary source, and instruction override attempts, checked before the prompt reaches the AI vendor.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>Before a tool runs</h4>
                <p>Credential file reads, writes to production paths, data exfiltration commands, database dumps, and destructive shell commands, stopped before execution.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>After a tool returns</h4>
                <p>Command output, file contents, and fetched web responses scanned for secrets and personal data before the model consumes them.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>When instructions load</h4>
                <p>Project rules files inspected for embedded policy overrides and permission escalation as the session starts.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>After context compaction</h4>
                <p>The generated summary scanned for secrets and personal data that survived compression into the next turn.</p>
              </div>
              <div className="ucp-compliance-card">
                <h4>On the response, and at session close</h4>
                <p>The model&apos;s answer checked before the developer sees it, and a session record written covering files changed, tools used, and commands run.</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div id="section-cta" className="ucp-cta">
            <button className="ucp-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              See Coding Agent Protection in Action &rarr;
            </button>
          </div>

          <span className="ucp-back-link" onClick={() => { navigate('/resources'); window.scrollTo(0, 0); }} style={{ cursor: 'pointer' }}>
            &#8592; Back to Resources
          </span>
        </div>
      </section>
    </>
  );
};

export default CodingAgentsGuide;
